import React, { useEffect, useState } from 'react'
import { Download, X, Smartphone } from 'lucide-react'
import { showInstallPrompt, isInstallable, isAppInstalled } from '../../lib/pwa'

interface InstallPWAPromptProps {
  onDismiss?: () => void
  autoShow?: boolean
  position?: 'top' | 'bottom'
}

const InstallPWAPrompt: React.FC<InstallPWAPromptProps> = ({
  onDismiss,
  autoShow = true,
  position = 'bottom'
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isInstalling, setIsInstalling] = useState(false)

  useEffect(() => {
    // Check if prompt should be shown
    const checkPromptAvailability = () => {
      // Don't show if already installed
      if (isAppInstalled()) {
        return
      }

      // Don't show if user previously dismissed
      const dismissed = localStorage.getItem('pwa-prompt-dismissed')
      if (dismissed) {
        const dismissedTime = new Date(dismissed).getTime()
        const now = new Date().getTime()
        const daysSinceDismissed = (now - dismissedTime) / (1000 * 60 * 60 * 24)

        // Show again after 7 days
        if (daysSinceDismissed < 7) {
          return
        }
      }

      // Check if installable
      if (autoShow && isInstallable()) {
        // Small delay before showing
        setTimeout(() => setIsVisible(true), 2000)
      }
    }

    checkPromptAvailability()

    // Listen for install availability
    const handleInstallAvailable = () => {
      if (autoShow && !isAppInstalled()) {
        setTimeout(() => setIsVisible(true), 2000)
      }
    }

    const handleInstalled = () => {
      setIsVisible(false)
    }

    window.addEventListener('pwa-install-available', handleInstallAvailable)
    window.addEventListener('pwa-installed', handleInstalled)

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable)
      window.removeEventListener('pwa-installed', handleInstalled)
    }
  }, [autoShow])

  const handleInstall = async () => {
    setIsInstalling(true)

    try {
      const result = await showInstallPrompt()

      if (result === 'accepted') {
        setIsVisible(false)
      } else if (result === 'dismissed') {
        // User dismissed, don't show again for a while
        handleDismiss()
      }
    } catch (error) {
      console.error('Install failed:', error)
    } finally {
      setIsInstalling(false)
    }
  }

  const handleDismiss = () => {
    setIsVisible(false)
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString())
    onDismiss?.()
  }

  if (!isVisible) {
    return null
  }

  return (
    <div
      className={`fixed left-4 right-4 z-50 animate-slide-up ${
        position === 'top' ? 'top-4' : 'bottom-4'
      }`}
      role="dialog"
      aria-labelledby="install-prompt-title"
      aria-describedby="install-prompt-description"
    >
      <div className="max-w-md mx-auto bg-gradient-to-r from-secret-primary to-[#c41e3a] rounded-lg shadow-2xl p-4">
        <div className="flex items-start space-x-3">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-md">
              <Smartphone className="text-secret-primary" size={24} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3
              id="install-prompt-title"
              className="text-sm font-bold text-white mb-1"
            >
              Add SECRET to Home Screen
            </h3>
            <p
              id="install-prompt-description"
              className="text-xs text-white/90 mb-3"
            >
              Access your orders offline with one tap. No app store required.
            </p>

            {/* Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handleInstall}
                disabled={isInstalling}
                className="flex-1 bg-white hover:bg-gray-100 text-secret-primary font-medium text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isInstalling ? (
                  <span>Installing...</span>
                ) : (
                  <>
                    <Download size={16} />
                    <span>Install</span>
                  </>
                )}
              </button>

              <button
                onClick={handleDismiss}
                className="px-3 py-2 text-white hover:text-white/80 transition-colors"
                aria-label="Dismiss install prompt"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Progress indicator (iOS Safari specific) */}
        {isInstalling && (
          <div className="mt-3 pt-3 border-t border-white/20">
            <p className="text-xs text-white/80 text-center">
              Follow your browser's prompts to complete installation
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default InstallPWAPrompt
